// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FilterMenu } from '@/components/filter-menu';
import {
  CRM_SYNC_STATUSES,
  CRM_SYNC_STATUS_LABELS,
  CrmSyncStatus,
} from '@/constants/crm-sync-status';
import { hasDriftDetectionIntegration } from '@/integrations/drift-support';

interface Props {
  /**
   * The currently selected sync status filter values.
   */
  value: CrmSyncStatus[];

  /**
   * A callback fired when the selected sync statuses change.
   */
  onChange: (value: CrmSyncStatus[]) => void;
}

/**
 * Multi-select filter for CRM sync status. Renders nothing unless the
 * organization is connected to a drift-detection CRM, since the in-sync /
 * out-of-sync / not-linked distinction only exists for drift-capable
 * integrations.
 */
export function SyncStatusFilterMenu(props: Props) {
  const { user } = useCurrentUser();

  if (!hasDriftDetectionIntegration(user.organization.enabledIntegrations)) {
    return null;
  }

  const options = CRM_SYNC_STATUSES.map((status) => ({
    id: status,
    label: CRM_SYNC_STATUS_LABELS[status],
    isActive: props.value.includes(status),
  }));

  const handleChangeOption = (id: CrmSyncStatus, isActive: boolean) => {
    if (isActive) {
      props.onChange([...props.value, id]);
    } else {
      props.onChange(props.value.filter((value) => value !== id));
    }
  };

  return (
    <FilterMenu
      label="Sync status"
      options={options}
      onChangeOption={handleChangeOption}
    />
  );
}
